import { Clock } from 'lucide-react';
import type { ReactNode } from 'react';
import { Avatar } from './avatar.js';
import { cn } from '../lib/cn.js';

/**
 * De kop van een bericht: wie het schreef, van welke afdeling, wanneer en aan wie.
 *
 * Dezelfde kop op de kaart in de tijdlijn (home-spec §9.1) en op de detailpagina
 * (bericht-detail-spec §4.1) — niet twee keer gebouwd met andere maten. Het enige verschil tussen
 * de twee plekken is de **titel** eronder (15 px tegenover 22), en die staat buiten dit onderdeel.
 *
 * **Een omschrijving van de doelgroep, nooit een lijst namen** (ADR-0014). "Iedereen" of "Ploeg
 * Nacht" zegt wat een lezer moet weten; wie er precies in zat, is niemands zaak.
 *
 * De rechterkant is een slot en geen vaste inhoud: op de kaart hangt daar een badge en het
 * "…"-menu, op de detailpagina alleen het menu. Zou dit onderdeel die kennen, dan moest het weten
 * wat een bevestiging is.
 */
export interface PostHeadProps {
  author: { name: string; department: string | null } | null;
  /** Al opgemaakt: "2 uur geleden", "Gisteren". Deze component rekent niet met tijd. */
  time: string;
  /** "Iedereen", "Ploeg Nacht". Leeg laten mag. */
  audience?: string | null;
  /** Rechts in de kop: een badge, een menu, of niets. */
  right?: ReactNode;
  className?: string;
}

export function PostHead({ author, time, audience, right, className }: PostHeadProps) {
  return (
    <div className={cn('flex items-start gap-3 px-5 pb-3 pt-[18px]', className)}>
      {author ? <Avatar name={author.name} size="lg" /> : null}

      <div className="min-w-0 flex-1">
        {author ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-heading text-content font-semibold text-text-primary">
              {author.name}
            </span>
            {author.department ? (
              <span className="rounded-pill bg-brand-primary-tint px-2 py-px font-heading text-micro font-semibold tracking-normal text-brand-primary-dark">
                {author.department}
              </span>
            ) : null}
          </div>
        ) : null}

        <span className="mt-0.5 flex items-center gap-1 text-caption text-text-muted">
          <Clock aria-hidden className="h-[11px] w-[11px] shrink-0" />
          {time}
          {audience ? <span className="truncate">{'· ' + audience}</span> : null}
        </span>
      </div>

      {right}
    </div>
  );
}
